"use client";

import { Drawer as DrawerPrimitive } from "@base-ui/react/drawer";
import { X } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

import { cn } from "@/lib/utils/cn";

type DrawerSide = "left" | "right" | "top" | "bottom";

const SWIPE_DIRECTION: Record<DrawerSide, "left" | "right" | "up" | "down"> = {
  left: "left",
  right: "right",
  top: "up",
  bottom: "down",
};

const POPUP_BY_SIDE: Record<DrawerSide, string> = {
  left: "h-dvh w-[min(28rem,calc(100vw-3rem))] border-r data-[ending-style]:[transform:translateX(-100%)] data-[starting-style]:[transform:translateX(-100%)]",
  right:
    "h-dvh w-[min(28rem,calc(100vw-3rem))] border-l data-[ending-style]:[transform:translateX(100%)] data-[starting-style]:[transform:translateX(100%)]",
  top: "max-h-[85dvh] w-full rounded-b-[1.75rem] border-b data-[ending-style]:[transform:translateY(-100%)] data-[starting-style]:[transform:translateY(-100%)]",
  bottom:
    "max-h-[95dvh] w-full rounded-t-[1.75rem] border-t data-[ending-style]:[transform:translateY(100%)] data-[starting-style]:[transform:translateY(100%)]",
};

const VIEWPORT_BY_SIDE: Record<DrawerSide, string> = {
  left: "flex-row justify-start",
  right: "flex-row justify-end",
  top: "flex-col justify-start",
  bottom: "flex-col justify-end",
};

interface DrawerProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  side?: DrawerSide;
  /** Small uppercase eyebrow rendered above the title. */
  eyebrow?: ReactNode;
  title?: ReactNode;
  /** Rendered below the title. */
  description?: ReactNode;
  /** Optional custom header — replaces eyebrow/title/description block when provided. */
  header?: ReactNode;
  /** Sticky footer slot. */
  footer?: ReactNode;
  trigger?: ReactNode;
  children?: ReactNode;
  className?: string;
  /** Inline style applied to the drawer popup. Use to override max-height. */
  style?: CSSProperties;
  contentClassName?: string;
  padding?: "default" | "none";
  showCloseButton?: boolean;
  hideHandle?: boolean;
}

export function Drawer({
  open,
  defaultOpen,
  onOpenChange,
  side = "bottom",
  eyebrow,
  title,
  description,
  header,
  footer,
  trigger,
  children,
  className,
  style,
  contentClassName,
  padding = "default",
  showCloseButton = true,
  hideHandle,
}: DrawerProps) {
  const isHorizontal = side === "left" || side === "right";
  const showDefaultHeader =
    !header && (eyebrow || title || description || showCloseButton);

  return (
    <DrawerPrimitive.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange ? (next) => onOpenChange(next) : undefined}
      swipeDirection={SWIPE_DIRECTION[side]}
    >
      {trigger ? (
        <DrawerPrimitive.Trigger render={trigger as React.ReactElement} />
      ) : null}
      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Backdrop className="fixed inset-0 z-40 min-h-dvh" />
        <DrawerPrimitive.Viewport
          className={cn("fixed inset-0 z-50 flex", VIEWPORT_BY_SIDE[side])}
        >
          <DrawerPrimitive.Popup
            className={cn(
              "flex flex-col border-paper-3 bg-paper text-ink shadow-[0_20px_40px_-10px_rgba(30,26,25,0.25)] outline-none overscroll-contain transition-transform duration-[450ms] ease-[cubic-bezier(0.32,0.72,0,1)] data-[swiping]:select-none data-[swiping]:duration-0",
              isHorizontal
                ? "[transform:translateX(var(--drawer-swipe-movement-x))]"
                : "[transform:translateY(var(--drawer-swipe-movement-y))]",
              POPUP_BY_SIDE[side],
              className,
            )}
            style={style}
          >
            {!hideHandle && !isHorizontal ? (
              <span
                aria-hidden
                className={cn(
                  "mx-auto h-1 w-11 shrink-0 rounded-full bg-ink/30",
                  side === "top" ? "order-last mt-2 mb-3" : "mt-3 mb-1",
                )}
              />
            ) : null}

            {header ? (
              <div
                className={cn(
                  "shrink-0",
                  padding === "default" ? "px-6 pt-4 pb-3" : undefined,
                )}
              >
                {header}
              </div>
            ) : showDefaultHeader ? (
              <div
                className={cn(
                  "flex items-start justify-between gap-3 shrink-0",
                  padding === "default" ? "px-6 pt-4 pb-3" : undefined,
                )}
              >
                <div className="flex flex-col gap-1">
                  {eyebrow ? (
                    <p className="font-mono text-xs uppercase tracking-[0.14em] text-ink-3">
                      {eyebrow}
                    </p>
                  ) : null}
                  {title ? (
                    <DrawerPrimitive.Title className="font-heading text-2xl leading-tight tracking-tight text-ink">
                      {title}
                    </DrawerPrimitive.Title>
                  ) : null}
                  {description ? (
                    <DrawerPrimitive.Description className="text-sm text-ink-3">
                      {description}
                    </DrawerPrimitive.Description>
                  ) : null}
                </div>
                {showCloseButton ? (
                  <DrawerPrimitive.Close
                    aria-label="Close"
                    className="inline-flex size-9 shrink-0 items-center justify-center rounded-full text-ink-2 transition-colors hover:bg-paper-2 hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
                  >
                    <X className="size-4" />
                  </DrawerPrimitive.Close>
                ) : null}
              </div>
            ) : null}

            <DrawerPrimitive.Content
              className={cn(
                "flex flex-1 flex-col gap-4 overflow-y-auto overscroll-contain",
                padding === "default" ? "px-6 py-4" : undefined,
                contentClassName,
              )}
            >
              {children}
            </DrawerPrimitive.Content>

            {footer ? (
              <div
                className={cn(
                  "shrink-0 border-t border-paper-3 bg-paper",
                  padding === "default" ? "px-6 py-4" : undefined,
                )}
              >
                {footer}
              </div>
            ) : null}
          </DrawerPrimitive.Popup>
        </DrawerPrimitive.Viewport>
      </DrawerPrimitive.Portal>
    </DrawerPrimitive.Root>
  );
}

export const DrawerClose = DrawerPrimitive.Close;
